// eslint-disable-next-line no-restricted-imports
import { Outlet, useNavigate } from '@remix-run/react'
import { useCallback } from 'react'
import { SdFooter } from '~/components/footer/sd'
import { SDHeader } from '~/components/header/sd/SDHeader'
import { Backdrop } from '~/components/modal/Backdrop'
import { SearchModeProvider } from '~/features/search/criteria/context'
import { SearchBox, SearchCalendarModal, SearchPreferencesModal } from '~/features/search/sd'
import { isEmpty } from '~/lib/empty'
import { searchToURL, useReserveOverlayContext } from '~/navigation'
import type { TopRootProps } from '~/routes/_index'
import { css, cva } from '~/styled-system/css'
import { Button } from '~/ui'
import { Announce } from '../Announce'
import { Areas } from '../Areas'
import { BackToYahooTop } from '../BackToYahooTop'
import { Criteria } from '../Criteria'
import { FooterBanners } from '../FooterBanners'
import { Genre } from '../Genre'
import { RecommendedRestaurants } from '../RecommendedRestaurants'
import { SpecialFeaturesAndCampaigns } from '../SpecialFeaturesAndCampaigns'
import { TopBanner } from '../TopBanner'
import { ApplyButton, useApplyCalendar } from './ApplyButton'
import { FilterBox } from './FilterBox'

const searchBoxWrapperStyle = cva({
  base: {
    position: 'relative',
    marginTop: '-8',
    backgroundColor: 'white',
    borderRadius: 'md',
    shadow: 'outline.md',
    padding: '1',
  },
  variants: {
    hasBanners: {
      false: { marginTop: '0' },
    },
  },
})

export function SDTopRoot({ data }: TopRootProps) {
  const hasBanners = !isEmpty(data.topBanners.banners)
  return (
    <>
      <Backdrop>
        <BackToYahooTop isFromYahooTop={data.isFromYahooTop} />
        <SDHeader />
        <TopBanner banners={data.topBanners.banners} />

        <div className={css({ p: '3' })}>
          <div className={searchBoxWrapperStyle({ hasBanners })}>
            <SearchBox id="search-box" filterBox={<FilterBox />} submitButton={<SubmitButton />} />
          </div>
        </div>
        <Announce />

        <div className={css({ divideY: '[12px]', divideColor: 'gray.100' })}>
          <RecommendedRestaurants data={data.recommendedRestaurants} />
          <Areas />
          <Genre genre={data.genre} />
          <Criteria criteria={data.criteriaContents} />
          <SpecialFeaturesAndCampaigns
            specialFeaturesAndCampaigns={data.topBanners.specialFeaturesAndCampaigns}
          />
        </div>
        <FooterBanners />
        <SdFooter type="full" />
      </Backdrop>
      <SearchCalendarModal applyButton={<ApplyButton useApply={useApplyCalendar} />} />
      <SearchModeProvider mode="save-only">
        <SearchPreferencesModal />
      </SearchModeProvider>
      <Outlet />
    </>
  )
}

function SubmitButton() {
  const context = useReserveOverlayContext('ROOT')
  // URL を組み立てているので、ここでは useNavigate で遷移する
  const navigate = useNavigate()
  const onClick = useCallback(() => {
    if (context !== undefined) {
      const to = searchToURL(context)
      navigate(to, { state: { stack: [], referrer: window.location.href } })
    }
  }, [context, navigate])
  if (context === undefined) {
    return null
  }
  return (
    <div className={css({ p: '3' })}>
      <Button color="primary" size="lg" spread onClick={onClick}>
        検索する
      </Button>
    </div>
  )
}
